<div class="container-xxl py-10">
  <!-- Back Button -->
  <div class="mb-5">
    <button class="btn btn-light" (click)="goBack()">
      <i class="fa fa-arrow-left me-2"></i>
      {{ 'trainerDetails.backToTrainers' | translate }}
    </button>
  </div>

  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'trainerDetails.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'trainerDetails.loading' | translate }}</p>
    </div>
  }

  <!-- Error State -->
  @if (error) {
    <div class="card">
      <div class="card-body text-center py-20">
        <i class="fa fa-exclamation-circle fs-3x text-danger mb-5"></i>
        <h3 class="text-muted mb-3">{{ error }}</h3>
        <button class="btn btn-primary" (click)="goBack()">
          {{ 'trainerDetails.backToTrainers' | translate }}
        </button>
      </div>
    </div>
  }

  <!-- Trainer Profile -->
  @if (!loading && !error && trainer) {
    <div class="row">
      <!-- Left Column - Profile Card -->
      <div class="col-lg-4 mb-5">
        <div class="card profile-card">
          <div class="card-body text-center">
            <!-- Profile Image -->
            <div class="profile-image-container">
              @if (trainer.profile_image_url) {
                <img [src]="trainer.profile_image_url" alt="{{ trainer.full_name }}" class="profile-image" />
              } @else {
                <div class="profile-initials">{{ getInitials() }}</div>
              }
            </div>

            <!-- Name -->
            <h2 class="profile-name">{{ trainer.full_name }}</h2>

            <!-- Location -->
            @if (getLocation()) {
              <div class="profile-location">
                <i class="fa fa-map-marker-alt me-2"></i>
                {{ getLocation() }}
              </div>
            }

            <!-- Social Media Links -->
            @if (hasSocialLinks()) {
              <div class="social-links mt-4">
                @if (trainer.trainer_website_url) {
                  <a [href]="trainer.trainer_website_url" target="_blank" rel="noopener noreferrer" class="social-icon-btn" title="Website">
                    <i class="fa fa-globe"></i>
                  </a>
                }
                @if (trainer.trainer_facebook_url) {
                  <a [href]="trainer.trainer_facebook_url" target="_blank" rel="noopener noreferrer" class="social-icon-btn social-facebook" title="Facebook">
                    <i class="fab fa-facebook-f"></i>
                  </a>
                }
                @if (trainer.trainer_instagram_url) {
                  <a [href]="trainer.trainer_instagram_url" target="_blank" rel="noopener noreferrer" class="social-icon-btn social-instagram" title="Instagram">
                    <i class="fab fa-instagram"></i>
                  </a>
                }
                @if (trainer.trainer_youtube_url) {
                  <a [href]="trainer.trainer_youtube_url" target="_blank" rel="noopener noreferrer" class="social-icon-btn social-youtube" title="YouTube">
                    <i class="fab fa-youtube"></i>
                  </a>
                }
                @if (trainer.trainer_tiktok_url) {
                  <a [href]="trainer.trainer_tiktok_url" target="_blank" rel="noopener noreferrer" class="social-icon-btn social-tiktok" title="TikTok">
                    <i class="fab fa-tiktok"></i>
                  </a>
                }
              </div>
            }

            <!-- Contact Button -->
            <div class="mt-5">
              <app-button
                [variant]="'primary'"
                [label]="'trainerDetails.sendMessage' | translate"
                [iconClass]="'fa fa-envelope'"
                [width]="'100%'"
                (clicked)="openMessageModal()"
              />
            </div>

            <!-- Recommend Trainer Button - visible to everyone -->
            <div class="mt-3">
              <app-button
                [variant]="'outline'"
                [label]="'trainer.recommendTrainer' | translate"
                [iconClass]="'fa fa-user-friends'"
                [width]="'100%'"
                (clicked)="openRecommendModal()"
              />
            </div>

            <!-- Apply for Trainer Button - only for logged-in non-trainer users -->
            @if (isLoggedIn() && !currentUserIsTrainer) {
              <div class="mt-3">
                <app-button
                  [variant]="'outline'"
                  [label]="'trainer.applyNow' | translate"
                  [iconClass]="'fa fa-paper-plane'"
                  [width]="'100%'"
                  (clicked)="openApplicationModal()"
                />
              </div>
            }

            <!-- Specializations -->
            @if (getSpecializationsList().length > 0) {
              <div class="profile-section mt-5">
                <h5 class="section-title">{{ 'trainerDetails.specializations' | translate }}</h5>
                <div class="specializations-list">
                  @for (spec of getSpecializationsList(); track spec) {
                    <span class="specialization-badge">{{ translateSpecialization(spec) }}</span>
                  }
                </div>
              </div>
            }

            <!-- Upcoming Events Section -->
            <div class="profile-section mt-5">
              <h5 class="section-title">{{ 'trainerDetails.upcomingEvents' | translate }}</h5>
              @if (loadingEvents) {
                <div class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">{{ 'trainerDetails.loading' | translate }}</span>
                  </div>
                </div>
              } @else if (upcomingEvents.length === 0) {
                <p class="text-muted small mb-0">{{ 'trainerDetails.noUpcomingEvents' | translate }}</p>
              } @else {
                <div class="event-list-compact">
                  @for (event of upcomingEvents; track event.event_id) {
                    <div class="event-item-compact" (click)="viewEvent(event)">
                      <div class="event-item-content">
                        <div class="event-name-compact">{{ event.event_name }}</div>
                        <div class="event-meta-compact">
                          @if (event.start_date) {
                            <span><i class="fa fa-calendar me-1"></i>{{ formatEventDate(event.start_date) }}</span>
                          }
                        </div>
                      </div>
                      <i class="fa fa-chevron-right event-arrow-compact"></i>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Past Events Section -->
            @if (pastEvents.length > 0) {
              <div class="profile-section mt-5">
                <h5 class="section-title">{{ 'trainerDetails.pastEvents' | translate }}</h5>
                <div class="event-list-compact">
                  @for (event of pastEvents; track event.event_id) {
                    <div class="event-item-compact past" (click)="viewEvent(event)">
                      <div class="event-item-content">
                        <div class="event-name-compact">{{ event.event_name }}</div>
                        <div class="event-meta-compact">
                          @if (event.start_date) {
                            <span><i class="fa fa-calendar me-1"></i>{{ formatEventDate(event.start_date) }}</span>
                          }
                        </div>
                      </div>
                      <i class="fa fa-chevron-right event-arrow-compact"></i>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Right Column - Details -->
      <div class="col-lg-8">
        <!-- Photo Gallery Section -->
        @if (images.length > 0 || loadingImages) {
          <div class="card mb-5">
            <div class="card-header">
              <h4 class="card-title mb-0">
                <i class="fa fa-images me-2 text-primary"></i>
                {{ 'trainerDetails.photos' | translate }}
              </h4>
            </div>
            <div class="card-body">
              @if (loadingImages) {
                <div class="text-center py-5">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">{{ 'trainerDetails.loading' | translate }}</span>
                  </div>
                </div>
              } @else {
                <div class="photo-gallery">
                  @for (image of images; track image.image_id; let i = $index) {
                    <div class="gallery-item" (click)="openLightbox(i)">
                      <img [src]="image.image_url" [alt]="image.caption || 'Trainer photo'" />
                      <div class="gallery-overlay">
                        <i class="fa fa-search-plus"></i>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }

        <!-- About Section -->
        @if (trainer.trainer_description) {
          <div class="card mb-5">
            <div class="card-header">
              <h4 class="card-title mb-0">
                <i class="fa fa-user me-2 text-primary"></i>
                {{ 'trainerDetails.about' | translate }}
              </h4>
            </div>
            <div class="card-body">
              <div class="description-content" [innerHTML]="trainer.trainer_description"></div>
            </div>
          </div>
        }

        <!-- Experience Section -->
        @if (trainer.trainer_experience) {
          <div class="card mb-5">
            <div class="card-header">
              <h4 class="card-title mb-0">
                <i class="fa fa-clock me-2 text-primary"></i>
                {{ 'trainerDetails.experience' | translate }}
              </h4>
            </div>
            <div class="card-body">
              <p class="mb-0">{{ trainer.trainer_experience }}</p>
            </div>
          </div>
        }

        <!-- Certificates Section -->
        @if (getCertificatesList().length > 0) {
          <div class="card mb-5">
            <div class="card-header">
              <h4 class="card-title mb-0">
                <i class="fa fa-certificate me-2 text-primary"></i>
                {{ 'trainerDetails.certificates' | translate }}
              </h4>
            </div>
            <div class="card-body">
              <ul class="certificates-list">
                @for (cert of getCertificatesList(); track cert) {
                  <li>
                    <i class="fa fa-check-circle me-2 text-success"></i>
                    {{ cert }}
                  </li>
                }
              </ul>
            </div>
          </div>
        }

        <!-- Achievements Section -->
        @if (getAchievementsList().length > 0) {
          <div class="card mb-5">
            <div class="card-header">
              <h4 class="card-title mb-0">
                <i class="fa fa-trophy me-2 text-primary"></i>
                {{ 'trainerDetails.achievements' | translate }}
              </h4>
            </div>
            <div class="card-body">
              <ul class="achievements-list">
                @for (ach of getAchievementsList(); track ach) {
                  <li>
                    <i class="fa fa-star me-2 text-warning"></i>
                    {{ ach }}
                  </li>
                }
              </ul>
            </div>
          </div>
        }

        <!-- Empty State if no details -->
        @if (!trainer.trainer_description && !trainer.trainer_experience && getCertificatesList().length === 0 && getAchievementsList().length === 0) {
          <div class="card">
            <div class="card-body text-center py-10">
              <i class="fa fa-info-circle fs-2x text-muted mb-5"></i>
              <p class="text-muted mb-0">{{ 'trainerDetails.noProfileInfo' | translate }}</p>
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>

<!-- Message Modal -->
@if (showMessageModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ 'trainerDetails.modal.title' | translate }} {{ trainer?.full_name }}</h5>
          <button type="button" class="btn-close" (click)="closeMessageModal()"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">{{ 'trainerDetails.modal.messageLabel' | translate }}</label>
            <textarea
              class="form-control"
              rows="5"
              [(ngModel)]="messageText"
              [placeholder]="'trainerDetails.modal.messagePlaceholder' | translate"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" (click)="closeMessageModal()">
            {{ 'trainerDetails.modal.cancel' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            [disabled]="sendingMessage || !messageText.trim()"
            (click)="sendMessage()"
          >
            @if (sendingMessage) {
              <span class="spinner-border spinner-border-sm me-2"></span>
              {{ 'trainerDetails.modal.sending' | translate }}
            } @else {
              <i class="fa fa-paper-plane me-2"></i>
              {{ 'trainerDetails.sendMessage' | translate }}
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Recommendation Modal -->
@if (showRecommendModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-user-friends me-2 text-success"></i>
            {{ 'trainer.recommendTitle' | translate }}
          </h5>
          <button type="button" class="btn-close" (click)="closeRecommendModal()"></button>
        </div>
        <div class="modal-body">
          <!-- Trainer Name -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.trainerName' | translate }} <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="recommendForm.trainer_name"
              [placeholder]="'trainer.trainerNamePlaceholder' | translate"
            />
          </div>

          <!-- Why Recommend -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.whyRecommend' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="4"
              [(ngModel)]="recommendForm.description"
              [placeholder]="'trainer.whyRecommendPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Specializations -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.specializations' | translate }}</label>
            <div class="specializations-selector mb-2">
              @for (spec of availableSpecializations; track spec) {
                <button
                  type="button"
                  class="specialization-btn"
                  [class.active]="isRecommendSpecializationSelected(spec)"
                  (click)="toggleRecommendSpecialization(spec)"
                >
                  {{ translateSpecialization(spec) }}
                </button>
              }
            </div>
          </div>

          <!-- Contact Info -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.contactInfo' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="recommendForm.contact_info"
              [placeholder]="'trainer.contactInfoPlaceholder' | translate"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" (click)="closeRecommendModal()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" class="btn btn-success" (click)="submitRecommendation()" [disabled]="submittingRecommendation">
            @if (submittingRecommendation) {
              <span class="spinner-border spinner-border-sm me-2"></span>
            }
            {{ 'trainer.submitRecommendation' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Application Modal -->
@if (showApplicationModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fa fa-paper-plane me-2 text-primary"></i>
            {{ 'trainer.applicationTitle' | translate }}
          </h5>
          <button type="button" class="btn-close" (click)="closeApplicationModal()"></button>
        </div>
        <div class="modal-body">
          <!-- About You -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.aboutYou' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="4"
              [(ngModel)]="applicationForm.trainer_description"
              [placeholder]="'trainer.aboutYouPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Experience -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.experience' | translate }} <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="3"
              [(ngModel)]="applicationForm.trainer_experience"
              [placeholder]="'trainer.experiencePlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Specializations -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.specializations' | translate }}</label>
            <div class="specializations-selector mb-2">
              @for (spec of availableSpecializations; track spec) {
                <button
                  type="button"
                  class="specialization-btn"
                  [class.active]="isApplicationSpecializationSelected(spec)"
                  (click)="toggleApplicationSpecialization(spec)"
                >
                  {{ translateSpecialization(spec) }}
                </button>
              }
            </div>
          </div>

          <!-- Certificates -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.certificates' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="applicationForm.trainer_certificates"
              [placeholder]="'trainer.certificatesPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Achievements -->
          <div class="mb-4">
            <label class="form-label fw-bold">{{ 'trainer.achievements' | translate }}</label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="applicationForm.trainer_achievements"
              [placeholder]="'trainer.achievementsPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Contact Info -->
          <div class="row">
            <div class="col-md-6 mb-4">
              <label class="form-label fw-bold">{{ 'trainer.contactEmail' | translate }}</label>
              <input
                type="email"
                class="form-control"
                [(ngModel)]="applicationForm.contact_email"
                [placeholder]="'trainer.contactEmailPlaceholder' | translate"
              />
            </div>
            <div class="col-md-6 mb-4">
              <label class="form-label fw-bold">{{ 'trainer.contactPhone' | translate }}</label>
              <input
                type="tel"
                class="form-control"
                [(ngModel)]="applicationForm.contact_phone"
                [placeholder]="'trainer.contactPhonePlaceholder' | translate"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" (click)="closeApplicationModal()">
            {{ 'common.cancel' | translate }}
          </button>
          <button type="button" class="btn btn-primary" (click)="submitApplication()" [disabled]="submittingApplication">
            @if (submittingApplication) {
              <span class="spinner-border spinner-border-sm me-2"></span>
            }
            {{ 'trainer.submitApplication' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Lightbox Modal -->
@if (showLightbox && images.length > 0) {
  <div class="lightbox-backdrop" (click)="closeLightbox()"></div>
  <div class="lightbox-container">
    <button class="lightbox-close" (click)="closeLightbox()">
      <i class="fa fa-times"></i>
    </button>

    @if (images.length > 1) {
      <button class="lightbox-nav lightbox-prev" (click)="prevImage(); $event.stopPropagation()">
        <i class="fa fa-chevron-left"></i>
      </button>
      <button class="lightbox-nav lightbox-next" (click)="nextImage(); $event.stopPropagation()">
        <i class="fa fa-chevron-right"></i>
      </button>
    }

    <div class="lightbox-content" (click)="$event.stopPropagation()">
      <img [src]="images[currentImageIndex].image_url" [alt]="images[currentImageIndex].caption || 'Trainer photo'" />
      @if (images[currentImageIndex].caption) {
        <div class="lightbox-caption">{{ images[currentImageIndex].caption }}</div>
      }
    </div>

    @if (images.length > 1) {
      <div class="lightbox-counter">
        {{ currentImageIndex + 1 }} / {{ images.length }}
      </div>
    }
  </div>
}
